/* eslint-disable prettier/prettier */
/* eslint-disable no-restricted-syntax */
/* eslint-disable simple-import-sort/imports */
/* eslint-disable react/function-component-definition */
import { createContext, useContext, useState, useEffect, ReactNode, useMemo } from 'react';

// Import translation files
import enTranslations from '../translations/en.json';
import deTranslations from '../translations/de.json';

export type Language = 'en' | 'de';

export interface LanguageContextType {
  language: Language;
  setLanguage: (lang: Language) => void;
  t: (key: string, params?: Record<string, string | number>) => string;
}

// Create translations object from imported JSON files
const translations = {
  en: enTranslations,
  de: deTranslations,
} as const;

const LanguageContext = createContext<LanguageContextType | undefined>(undefined);

interface LanguageProviderProps {
  children: ReactNode;
  defaultLanguage?: Language;
}

export const LanguageProvider = ({ children, defaultLanguage = 'en' }: LanguageProviderProps) => {
  const [language, setLanguage] = useState<Language>(() => {
    // Try to get language from localStorage, fallback to default
    const stored = localStorage.getItem('language') as Language;
    return stored || defaultLanguage;
  });

  useEffect(() => {
    // Store language preference in localStorage
    localStorage.setItem('language', language);
  }, [language]);

  const t = useMemo(
    () =>
      (key: string, params?: Record<string, string | number>): string => {
        const keys = key.split('.');
        let value: unknown = translations[language];

        // Navigate through nested object structure
        for (const k of keys) {
          value = (value as Record<string, unknown>)?.[k];
        }

        if (value === undefined) {
          console.warn(`Translation key "${key}" not found for language "${language}"`);
          return key; // Fallback to key if translation not found
        }

        // Replace parameters in the string
        if (params && typeof value === 'string') {
          return value.replace(/\{\{(\w+)\}\}/g, (match, param) => {
            return String(params[param] ?? match);
          });
        }

        return value as string;
      },
    [language]
  );

  const contextValue = useMemo(
    () => ({
      language,
      setLanguage,
      t,
    }),
    [language, setLanguage, t]
  );

  return (
    <LanguageContext.Provider value={contextValue}>
      {children}
    </LanguageContext.Provider>
  );
};

export const useLanguage = (): LanguageContextType => {
  const context = useContext(LanguageContext);
  if (context === undefined) {
    throw new Error('useLanguage must be used within a LanguageProvider');
  }
  return context;
};
